import type { QueryClient } from "@tanstack/react-query";
import type { Api } from "../../data/api";
import type { PaletteItem } from "../../components/shell/CommandPalette";
import { loadAgenturen } from "./data";
import type { KonsolenRolle } from "./konsole-nav";

/**
 * Suche der Betreiber-Konsole (⌘K / Strg+K, Vertrieb-Konsole Inhaber 02.11.2026): Konten (Name, Kürzel, Admin-Adresse),
 * Leads (Firma, Kontakt, E-Mail; ab 0460) und für Admins Tickets (Betreff, Agentur, Nummer). Konten und Tickets kommen aus
 * demselben Zwischenspeicher wie ihre Listen; ein Fehler in einem Teil leert nur diesen Teil.
 */
type Reader = Pick<Api, "read">;

const glatt = (s: string) => s.toLowerCase().replace(/ä/g, "ae").replace(/ö/g, "oe").replace(/ü/g, "ue").replace(/ß/g, "ss");
const parse = <T,>(r: unknown): T => (typeof r === "string" ? JSON.parse(r) : r) as T;

interface TicketTreffer { id: string; number: number; subject: string; org_name: string; status: string }
interface LeadTreffer { id: string; company: string | null; contact_name: string | null; email: string | null; stage_label: string }

export function trifft(text: string, q: string): boolean {
  const n = glatt(q.trim());
  return n.length > 0 && glatt(text).includes(n);
}

export async function konsoleSuche(qc: QueryClient, api: Reader, rolle: KonsolenRolle, q: string): Promise<PaletteItem[]> {
  const frage = q.trim();
  if (frage.length < 2) return [];
  const konten = qc.fetchQuery({ queryKey: ["platform_org_overview", "alle", "betrieb"], staleTime: 60_000, queryFn: () => loadAgenturen(api as Api) })
    .then((d) => d.rows.filter((r) => trifft(`${r.name} ${r.slug} ${r.chef_emails.join(" ")}`, frage)).slice(0, 6)
      .map((r): PaletteItem => ({ label: r.name, to: `/intern/agenturen/${r.org_id}`, hint: `Konto · ${r.chef_emails[0] ?? r.slug}` })))
    .catch(() => [] as PaletteItem[]);
  const leads = api.read("crm_search", { p_q: frage })
    .then((r) => parse<LeadTreffer[] | null>(r) ?? [])
    .then((rows) => rows.slice(0, 6).map((l): PaletteItem => ({
      label: l.company ?? l.contact_name ?? l.email ?? "Lead", to: `/intern/leads/${l.id}`,
      hint: `Lead · ${[l.contact_name, l.stage_label].filter(Boolean).join(" · ")}`,
    })))
    .catch(() => [] as PaletteItem[]);
  const tickets = rolle === "sales" ? Promise.resolve([] as PaletteItem[])
    : qc.fetchQuery({ queryKey: ["platform_support_tickets"], staleTime: 60_000,
      queryFn: async () => parse<TicketTreffer[]>(await api.read("platform_support_tickets", { p_days: 365 })) })
      .then((rows) => rows.filter((t) => trifft(`${t.subject} ${t.org_name} ${t.number}`, frage)).slice(0, 6)
        .map((t): PaletteItem => ({ label: `#${t.number} ${t.subject}`, to: `/intern/tickets/${t.id}`, hint: `Ticket · ${t.org_name}` })))
      .catch(() => [] as PaletteItem[]);
  const [a, b, c] = await Promise.all([konten, leads, tickets]);
  return [...a, ...b, ...c];
}
